/**
 * Service for managing KnowledgeItem storage using a pluggable StorageProvider.
 * All methods default to using localStorage via defaultStorageProvider, but accept DI for testability.
 */
import { KnowledgeItem } from '../../types/learning';
import {
  StorageProvider,
  defaultStorageProvider,
  LOCAL_STORAGE_KEYS,
  LOCAL_STORAGE_SCHEMA_VERSION,
  VersionedEnvelope,
  readLocalProfile,
  writeLocalProfile,
  migrateLocalProfile,
} from './localStorageService';

/** Storage key for all knowledge items */
export const KNOWLEDGE_ITEMS_KEY = 'KNOWLEDGE_ITEMS';

const asVersioned = <T>(data: T): VersionedEnvelope<T> => ({
  storageVersion: LOCAL_STORAGE_SCHEMA_VERSION,
  data,
});

const isVersionedEnvelope = <T>(value: unknown): value is VersionedEnvelope<T> => {
  return !!value && typeof value === 'object' && 'storageVersion' in value && 'data' in value;
};

/**
 * Helper function to validate and fix FEN strings in move-type knowledge items
 * @param item KnowledgeItem to validate
 * @returns KnowledgeItem with validated FEN string if needed
 */
export function validateKnowledgeItem(item: KnowledgeItem): KnowledgeItem {
  // Only validate move-type items with content in the format "FEN|move"
  if (item.type === 'move' && typeof item.content === 'string' && item.content.includes('|')) {
    const [fen, move] = item.content.split('|');
    const validatedFen = validateFEN(fen);
    // Only update if the FEN was actually changed
    if (validatedFen !== fen) {
      return {
        ...item,
        content: `${validatedFen}|${move}`
      };
    }
  }
  return item;
}

/**
 * Helper function to validate and fix a FEN string, focusing on en-passant squares
 * @param fen FEN string to validate
 * @returns Fixed FEN string
 */
export function validateFEN(fen: string): string {
  if (!fen) return '';

  try {
    const parts = fen.split(' ');
    if (parts.length !== 6) return fen; // Not a standard FEN format

    // Check and fix the en-passant square (4th component)
    const enPassantIndex = 3;
    const enPassant = parts[enPassantIndex];

    // If it's not "-" and not a valid square (a-h)(1-8), replace with "-"
    if (enPassant !== '-' && !/^[a-h][1-8]$/.test(enPassant)) {
      // Invalid en-passant square, replace with "-"
      parts[enPassantIndex] = '-';
      return parts.join(' ');
    }

    return fen;
  } catch (error) {
    console.error('Error validating FEN:', error);
    return fen;
  }
}

const reviveKnowledgeItemDates = (rawItems: unknown): KnowledgeItem[] => {
  if (!Array.isArray(rawItems)) return [];

  return rawItems
    .filter((item): item is Record<string, unknown> => !!item && typeof item === 'object')
    .map((item) => {
      const normalized: KnowledgeItem = {
        ...(item as unknown as KnowledgeItem),
        lastReviewed: item.lastReviewed ? new Date(item.lastReviewed as string) : new Date(0),
        nextReviewDate: item.nextReviewDate ? new Date(item.nextReviewDate as string) : new Date(0),
      };
      return validateKnowledgeItem(normalized);
    });
};

const readLegacyKnowledgeItems = (storage: StorageProvider): KnowledgeItem[] => {
  const raw = storage.getItem(KNOWLEDGE_ITEMS_KEY);
  if (!raw) return [];

  try {
    const parsed = JSON.parse(raw);
    return isVersionedEnvelope<unknown[]>(parsed)
      ? reviveKnowledgeItemDates(parsed.data)
      : reviveKnowledgeItemDates(parsed);
  } catch {
    return [];
  }
};

const readKnowledgeItemsFromProfile = (storage: StorageProvider): KnowledgeItem[] => {
  const profile = readLocalProfile(storage);
  if (!profile?.knowledgeItems) return [];

  if (isVersionedEnvelope<unknown[]>(profile.knowledgeItems)) {
    return reviveKnowledgeItemDates(profile.knowledgeItems.data);
  }

  return reviveKnowledgeItemDates(profile.knowledgeItems as unknown[]);
};

const writeKnowledgeItems = (items: KnowledgeItem[], storage: StorageProvider): void => {
  const profile = migrateLocalProfile(storage);
  const nextProfile = {
    ...profile,
    storageVersion: LOCAL_STORAGE_SCHEMA_VERSION,
    knowledgeItems: asVersioned(items),
  };

  writeLocalProfile(nextProfile, storage);

  // Legacy mirror for backward compatibility.
  storage.setItem(KNOWLEDGE_ITEMS_KEY, JSON.stringify(nextProfile.knowledgeItems));
};

/**
 * Get all knowledge items from storage.
 * @param storage Optional storage provider (defaults to localStorage)
 * @returns Array of KnowledgeItem objects
 */
export function getAllKnowledgeItems(storage: StorageProvider = defaultStorageProvider): KnowledgeItem[] {
  try {
    migrateLocalProfile(storage);

    const profileItems = readKnowledgeItemsFromProfile(storage);
    if (profileItems.length > 0) {
      return profileItems;
    }

    const legacyItems = readLegacyKnowledgeItems(storage);
    if (legacyItems.length > 0) {
      writeKnowledgeItems(legacyItems, storage);
    }

    return legacyItems;
  } catch (error) {
    console.error('Error loading knowledge items:', error);
    return [];
  }
}

/**
 * Get a single knowledge item by ID.
 * @param id The knowledge item ID
 * @param storage Optional storage provider
 * @returns The KnowledgeItem or null if not found
 */
export function getKnowledgeItemById(id: string, storage: StorageProvider = defaultStorageProvider): KnowledgeItem | null {
  const items = getAllKnowledgeItems(storage);
  return items.find(item => item.id === id) || null;
}

/**
 * Save or update a knowledge item in storage.
 * @param item The KnowledgeItem to save
 * @param storage Optional storage provider
 */
export function saveKnowledgeItem(item: KnowledgeItem, storage: StorageProvider = defaultStorageProvider): void {
  const validatedItem = validateKnowledgeItem(item);

  const items = getAllKnowledgeItems(storage);
  const idx = items.findIndex(i => i.id === validatedItem.id);
  if (idx >= 0) {
    items[idx] = validatedItem;
  } else {
    items.push(validatedItem);
  }

  writeKnowledgeItems(items, storage);
}

/**
 * Delete a knowledge item by ID.
 * @param id The knowledge item ID
 * @param storage Optional storage provider
 */
export function deleteKnowledgeItem(id: string, storage: StorageProvider = defaultStorageProvider): void {
  const items = getAllKnowledgeItems(storage).filter(item => item.id !== id);
  writeKnowledgeItems(items, storage);
}

/**
 * Named export for all knowledge item service functions, for convenient import as an object.
 */
export const knowledgeItemService = {
  getAllKnowledgeItems,
  getKnowledgeItemById,
  saveKnowledgeItem,
  deleteKnowledgeItem,
  validateKnowledgeItem,
  validateFEN,
  KNOWLEDGE_ITEMS_KEY,
};
